import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Reveal } from '@/components/Reveal';
import { OrderedGallery } from '@/components/OrderedGallery';
import { ProjectCard } from '@/components/ProjectCard';
import { Paragraphs } from '@/components/ui/paragraphs';
import { useServices } from '@/data/useServices';
import { useProjects } from '@/data/useProjects';

export default function ProjectDetail() {
  const { slug } = useParams();
  const {
    getProjectBySlug,
    getRelatedProjects,
    loading: projectsLoading,
    error: projectsError,
  } = useProjects();
  const project = getProjectBySlug(slug);
  const {
    getServiceBySlug,
    services: SERVICES,
    loading: servicesLoading,
    error: servicesError,
  } = useServices();
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  // Related-projects slider state (hooks must be at top-level, before any early return)
  const sliderRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const service = project ? getServiceBySlug(project.category) : null;

  const related = useMemo(
    () => (project ? getRelatedProjects(project.slug, project.category, 50) : []),
    [getRelatedProjects, project],
  );

  // Keep the lightbox inputs referentially stable. Recreating these arrays on
  // every slider update can make a controlled lightbox reuse a stale index.
  const sectionsList = useMemo(() => project?.sections || [], [project]);
  const allSlideSrcs = useMemo(
    () => [
      ...sectionsList.flatMap((section) => section.images || []),
      ...(project?.gallery || []),
    ],
    [project, sectionsList],
  );
  const slides = useMemo(
    () => allSlideSrcs.map((src) => ({ src })),
    [allSlideSrcs],
  );

  const handleLightboxView = useCallback(({ index: currentIndex }) => {
    if (Number.isInteger(currentIndex)) {
      setIndex(currentIndex);
    }
  }, []);

  const openLightbox = useCallback((src) => {
    const nextIndex = allSlideSrcs.indexOf(src);
    setIndex(nextIndex >= 0 ? nextIndex : 0);
    setOpen(true);
  }, [allSlideSrcs]);

  const updateArrows = useCallback(() => {
    const el = sliderRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    updateArrows();
    const el = sliderRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [related.length, updateArrows]);

  // Auto-play the slider (only when more than 3 items). Pauses on hover / touch.
  useEffect(() => {
    // Do not let the background carousel trigger state updates while the
    // lightbox is open. The lightbox remains the only active navigation layer.
    if (open || related.length <= 3) return;
    const el = sliderRef.current;
    if (!el) return;
    let timer = null;
    let paused = false;
    const tick = () => {
      if (paused || !el) return;
      const firstItem = el.querySelector('[data-slide-item]');
      const step = firstItem ? firstItem.getBoundingClientRect().width + 24 : 320;
      const atEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - 4;
      if (atEnd) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: step, behavior: 'smooth' });
      }
    };
    const start = () => {
      if (timer) return;
      timer = setInterval(tick, 4500);
    };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    const pause = () => { paused = true; };
    const resume = () => { paused = false; };
    start();
    el.addEventListener('mouseenter', pause);
    el.addEventListener('mouseleave', resume);
    el.addEventListener('touchstart', pause, { passive: true });
    el.addEventListener('touchend', resume, { passive: true });
    return () => {
      stop();
      el.removeEventListener('mouseenter', pause);
      el.removeEventListener('mouseleave', resume);
      el.removeEventListener('touchstart', pause);
      el.removeEventListener('touchend', resume);
    };
  }, [open, related.length]);

  useEffect(() => {
    if (slides.length === 0) {
      setOpen(false);
      setIndex(0);
      return;
    }

    if (index >= slides.length) {
      setIndex(slides.length - 1);
    }
  }, [index, slides.length]);

  // Scroll directly to the banner on first mount (skip ¿QUÉ NECESITAS? + menu)
  useEffect(() => {
    if (!project) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="project-banner-${service?.slug}"]`);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 90; // offset for sticky header
        window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
      }
    }, 80);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.slug]);

  if ((projectsLoading && !project) || (servicesLoading && !service)) {
    return (
      <div className="min-h-[65vh] bg-[#EFECE7] flex items-center justify-center text-[#58677A]">
        <div className="w-12 h-12 rounded-full border-4 border-[#58677A]/15 border-t-[#ED6B24] animate-spin" aria-label="Cargando proyecto" />
      </div>
    );
  }

  if ((!project && projectsError) || (project && !service && servicesError)) {
    return (
      <div className="min-h-[65vh] bg-[#EFECE7] flex items-center justify-center px-6 text-center text-[#58677A]">
        <p className="max-w-lg">No pudimos cargar el contenido administrado en este momento. Recarga la página en unos segundos.</p>
      </div>
    );
  }

  if (!project) return <Navigate to="/servicios-proyectos" replace />;

  const scrollBy = (dir) => {
    const el = sliderRef.current;
    if (!el) return;
    const firstItem = el.querySelector('[data-slide-item]');
    const step = firstItem ? firstItem.getBoundingClientRect().width + 24 : 320;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };
  const showSlider = related.length > 3;

  return (
    <div
      className="text-[#58677A]"
      data-testid="project-detail-page"
      style={{
        backgroundColor: '#EFECE7',
        backgroundImage: "url('/assets/backgrounds/nosotros-bg.jpg')",
        backgroundRepeat: 'repeat-y',
        backgroundSize: '100% auto',
        backgroundPosition: 'top center',
      }}
    >
      {/* Top: ¿QUÉ NECESITAS? + centered category menu */}
      <section className="pt-16 pb-10">
        <div className="container-link">
          <Reveal>
            <h1
              className="text-center uppercase mb-10 lg:mb-12"
              style={{
                color: '#ED6B24',
                fontSize: 'clamp(22pt, 5.5vw, 30pt)',
                fontFamily: 'Gotham, sans-serif',
                fontWeight: 900,
                letterSpacing: '0.02em',
              }}
              data-testid="project-detail-title"
            >
              ¿QUÉ NECESITAS?
            </h1>
          </Reveal>

          {/* Category menu — 4 buttons per row on desktop; incomplete rows auto-center */}
          <div
            className="flex flex-wrap justify-center gap-2.5 mb-12 lg:mb-14 max-w-5xl mx-auto"
            data-testid="project-detail-category-menu"
          >
            {SERVICES.map((s) => {
              const active = s.slug === project.category;
              return (
                <Link
                  key={s.slug}
                  to={`/servicios/${s.slug}`}
                  className={`text-center whitespace-nowrap rounded-full px-3 py-1.5 text-[13px] leading-tight font-bold uppercase border-2 transition-all duration-200
                    basis-full sm:basis-[calc((100%-0.625rem)/2)] md:basis-[calc((100%-1.25rem)/3)] lg:basis-[calc((100%-1.875rem)/4)] ${
                    active
                      ? 'bg-[#ED6B24] text-white border-[#ED6B24]'
                      : 'bg-white text-[#ED6B24] border-[#ED6B24] hover:bg-[#ED6B24]/10'
                  }`}
                  data-testid={`project-detail-category-item-${s.slug}`}
                >
                  {s.name}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Banner (same as CategoryPage) */}
      {service && (
        <section className="pb-12">
          <div className="container-link">
            <div
              className="relative rounded-[28px] overflow-hidden border border-black/10"
              data-testid={`project-banner-${service.slug}`}
              style={{
                '--small-scale': ((service.hubBannerSizes?.smallPct || 100) / 100),
                '--main-scale': ((service.hubBannerSizes?.mainPct || 100) / 100),
              }}
            >
              <div
                className="absolute inset-0 bg-cover bg-right sm:bg-center"
                style={{
                  backgroundImage: `url(${service.hubBanner || service.bgImage})`,
                }}
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    'linear-gradient(110deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.20) 60%, rgba(0,0,0,0.10) 100%)',
                }}
              />
              <div
                className="relative px-6 sm:px-10 lg:px-14 py-5 sm:py-7 lg:py-9 flex items-center"
                style={{ minHeight: 'clamp(100px, 16vw, 195px)' }}
              >
                <div className="max-w-[70%] sm:max-w-[65%] lg:max-w-[60%] text-white">
                  {service.hubBannerText?.top && (
                    <p
                      className="uppercase font-semibold tracking-[0.04em] text-white/95 mb-1 sm:mb-2 leading-tight"
                      style={{ fontSize: `calc(clamp(11px, calc(11px + 5 * ((100vw - 640px) / 640)), 16px) * var(--small-scale, 1))` }}
                    >
                      {service.hubBannerText.top}
                    </p>
                  )}
                  <h2
                    className="uppercase font-black leading-[0.95] text-white tracking-tight"
                    style={{ fontSize: `calc(clamp(30px, calc(30px + 42 * ((100vw - 640px) / 640)), 72px) * var(--main-scale, 1))` }}
                  >
                    {service.hubBannerText?.main || service.name}
                  </h2>
                  {service.hubBannerText?.bottom && (
                    <p
                      className="uppercase font-semibold tracking-[0.04em] text-white/95 mt-1 sm:mt-2 leading-tight"
                      style={{ fontSize: `calc(clamp(11px, calc(11px + 5 * ((100vw - 640px) / 640)), 16px) * var(--small-scale, 1))` }}
                    >
                      {service.hubBannerText.bottom}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Title + Short description */}
      <section className="pb-8 lg:pb-10">
        <div className="container-link">
          <Reveal>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-black leading-[1.05] text-[#58677A] mb-4"
              data-testid="project-detail-name"
            >
              {project.title}
            </h2>
            {project.subtitle && (
              <p className="text-sm sm:text-base text-[#58677A]/70 mb-3">{project.subtitle}</p>
            )}
            <Paragraphs
              text={project.description}
              className="text-base sm:text-lg text-[#58677A]/85 leading-relaxed"
              spacingClass="mb-3 last:mb-0"
              testId="project-description"
            />
          </Reveal>
        </div>
      </section>

      {/* Sections (multi-gallery) rendered first — each with its own title + masonry */}
      {sectionsList.length > 0 && sectionsList.map((sec, sIdx) => (
        <section key={sec.id} className="pb-4 lg:pb-6" data-testid={`project-section-${sec.id}`}>
          <div className="container-link">
            {sec.title && (
              <Reveal>
                <h3
                  className="text-lg sm:text-xl lg:text-2xl font-black uppercase text-[#58677A] mb-4 lg:mb-6"
                  data-testid={`project-section-title-${sIdx}`}
                >
                  {sec.title}
                </h3>
              </Reveal>
            )}
            {(sec.images || []).length > 0 && (
              <OrderedGallery
                images={sec.images || []}
                altPrefix={sec.title || project.title}
                testId={`project-section-masonry-${sIdx}`}
                itemTestIdPrefix={`project-section-item-${sIdx}`}
                onImageClick={(src) => openLightbox(src)}
              />
            )}
          </div>
        </section>
      ))}

      {/* Masonry Gallery (single/flat, rendered last) */}
      {(project.gallery || []).length > 0 && (
      <section className="pb-4 lg:pb-6">
        <div className="container-link">
          <OrderedGallery
            images={project.gallery}
            altPrefix={project.title}
            testId="project-gallery-masonry"
            itemTestIdPrefix="project-gallery-item"
            onImageClick={(src) => openLightbox(src)}
          />
        </div>
      </section>
      )}

      <Lightbox
        open={open}
        close={() => setOpen(false)}
        index={index}
        slides={slides}
        on={{ view: handleLightboxView }}
        controller={{ closeOnBackdropClick: true }}
      />

      {/* Related */}
      {related.length > 0 && (
        <section className="section-pad !pt-0">
          <div className="container-link">
            <Reveal>
              <h3
                className="text-xl sm:text-2xl lg:text-3xl font-black uppercase mb-8 lg:mb-10 text-[#58677A]"
                data-testid="project-related-title"
              >
                {service?.moreTitle}
              </h3>
            </Reveal>
            {showSlider ? (
              <div className="relative" data-testid="related-slider-wrap">
                <div
                  ref={sliderRef}
                  className="flex gap-6 lg:gap-8 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                  data-testid="related-slider"
                >
                  {related.map((p, idx) => (
                    <div
                      key={p.slug}
                      data-slide-item
                      className="snap-start shrink-0 basis-[85%] sm:basis-[calc((100%-1.5rem)/2)] lg:basis-[calc((100%-4rem)/3)]"
                    >
                      <Reveal delay={idx * 0.05}>
                        <ProjectCard project={p} testId="related-project-card" />
                      </Reveal>
                    </div>
                  ))}
                </div>
                {/* Side arrows — positioned outside the slider, hidden on mobile (touch swipe available) */}
                <button
                  onClick={() => scrollBy(-1)}
                  disabled={!canScrollLeft}
                  className="hidden sm:flex absolute top-1/2 -translate-y-1/2 -left-3 lg:-left-6 z-10 w-11 h-11 lg:w-12 lg:h-12 rounded-full bg-white shadow-[0_6px_20px_rgba(0,0,0,0.15)] border border-[#58677A]/15 items-center justify-center text-[#58677A] hover:bg-[#ED6B24] hover:text-white hover:border-[#ED6B24] disabled:opacity-30 disabled:pointer-events-none transition-colors"
                  aria-label="Anterior"
                  data-testid="related-arrow-left"
                >
                  <ChevronLeft size={22} />
                </button>
                <button
                  onClick={() => scrollBy(1)}
                  disabled={!canScrollRight}
                  className="hidden sm:flex absolute top-1/2 -translate-y-1/2 -right-3 lg:-right-6 z-10 w-11 h-11 lg:w-12 lg:h-12 rounded-full bg-white shadow-[0_6px_20px_rgba(0,0,0,0.15)] border border-[#58677A]/15 items-center justify-center text-[#58677A] hover:bg-[#ED6B24] hover:text-white hover:border-[#ED6B24] disabled:opacity-30 disabled:pointer-events-none transition-colors"
                  aria-label="Siguiente"
                  data-testid="related-arrow-right"
                >
                  <ChevronRight size={22} />
                </button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {related.map((p, idx) => (
                  <Reveal key={p.slug} delay={idx * 0.05}>
                    <ProjectCard project={p} testId="related-project-card" />
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
